import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";

import CustomModal from "../CustomModal";
import PlacesAutocomplete from "../PlacesAutocomplete";
import { API } from "../../../utils/api";
import { GOOGLE_API_KEY } from "../../../utils/env";
import { convertTimeTo12h, showToast } from "../../../utils/helper";
import { trackEvent } from "../../../utils/analytics";
import {
  compactVehicleImg,
  standardVehicleImg,
  suvVehicleImg,
} from "../../../Constants/images";

const VEHICLE_TYPES = [
  { value: "compact", label: "Compact", img: compactVehicleImg },
  { value: "standard", label: "Standard", img: standardVehicleImg },
  { value: "suv", label: "SUV", img: suvVehicleImg },
];

const DAYS = [
  { key: "Sun", label: "S" },
  { key: "Mon", label: "M" },
  { key: "Tue", label: "T" },
  { key: "Wed", label: "W" },
  { key: "Thu", label: "T" },
  { key: "Fri", label: "F" },
  { key: "Sat", label: "S" },
];

const defaultAvailability = DAYS.map((d) => ({ day: d.key, hours: [] }));

const FULL_DAY_NAMES = {
  Sun: "Sunday",
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
};
const dayFullName = (key) => FULL_DAY_NAMES[key] || key;

const buildInitial = (spot) => {
  if (!spot) {
    return {
      id: null,
      name: "",
      address: "",
      price: "",
      isFlatRate: false,
      flatRate: "",
      vehicleType: { compact: false, standard: false, suv: false },
      location: { type: "Point", coordinates: [] },
      status: "published",
      currentDayOnly: false,
      requiresApproval: true,
      hasEvCharger: false,
      images: [],
      availabilityMode: "recurring",
      availabilities: defaultAvailability.map((a) => ({ ...a, hours: [] })),
      dateAvailabilities: [],
    };
  }

  const allowed = spot.allowedVehicleType || {};
  const coords =
    spot.location && Array.isArray(spot.location.coordinates)
      ? spot.location.coordinates
      : [];

  const availabilities = groupAvailabilityFromServer(spot.availabilities);

  const images = (spot.images || []).map((url) => ({
    url, // existing remote image - no File
    isExisting: true,
  }));

  return {
    id: spot.id || null,
    name: spot.name || "",
    address: spot.address || "",
    price: spot.ratePerHour ? String(spot.ratePerHour) : "",
    isFlatRate: spot.isFlatRate === true || spot.isFlatRate === "true",
    flatRate: spot.flatRate ? String(spot.flatRate) : "",
    vehicleType: {
      compact: isVehicleTypeOn(allowed.compact),
      standard: isVehicleTypeOn(allowed.standard),
      suv: isVehicleTypeOn(allowed.suv),
    },
    location: {
      type: "Point",
      coordinates: coords.length === 2 ? [coords[0], coords[1]] : [],
    },
    status: spot.status || "published",
    currentDayOnly: Boolean(spot.currentDayOnly),
    requiresApproval:
      spot.requiresApproval == null ? true : Boolean(spot.requiresApproval),
    hasEvCharger: Boolean(spot.hasEvCharger),
    images,
    availabilityMode: spot.availabilityMode || "recurring",
    availabilities,
    dateAvailabilities: Array.isArray(spot.dateAvailabilities)
      ? spot.dateAvailabilities.map((d) => ({
          date: d.date,
          startTime: d.startTime,
          endTime: d.endTime,
        }))
      : [],
  };
};

const overlaps = (slots, newStart, newEnd, excludeIndex = -1) =>
  slots.some((s, idx) => {
    if (idx === excludeIndex) return false;
    return newStart < s.endTime && newEnd > s.startTime;
  });

// Server sometimes returns vehicleType values as 'true'/'false' strings.
const isVehicleTypeOn = (v) =>
  (typeof v === "string" && v === "true") || v === true;

// Server can return availabilities either grouped or as a flat list
// of {day, startTime, endTime, id}. Group it into the per-day shape
// the form uses.
const groupAvailabilityFromServer = (input) => {
  const byDay = {};
  DAYS.forEach((d) => {
    byDay[d.key] = [];
  });

  if (!Array.isArray(input))
    return DAYS.map((d) => ({ day: d.key, hours: [] }));

  input.forEach((entry) => {
    if (!entry || !entry.day) return;
    if (Array.isArray(entry.hours)) {
      byDay[entry.day] = entry.hours.map((h) => ({
        id: h.id,
        startTime: h.startTime,
        endTime: h.endTime,
      }));
    } else if (entry.startTime && entry.endTime) {
      byDay[entry.day] = byDay[entry.day] || [];
      byDay[entry.day].push({
        id: entry.id,
        startTime: entry.startTime,
        endTime: entry.endTime,
      });
    }
  });

  return DAYS.map((d) => ({ day: d.key, hours: byDay[d.key] || [] }));
};

// extraFields + apiOverride are an unauthenticated-claim escape hatch used
// by ClaimSpotModal so the landing-page flow can append name/email and
// post to /spot/claim without forking the whole form.
const AddSpotModal = ({
  show,
  onClose,
  onSuccess,
  spot = null,
  extraFields = null,
  apiOverride = null,
  successLabel = null,
  // Optional slot rendered at the top of the Details tab. Used by the
  // unauthenticated landing-page claim flow to inject Name + Email inline
  // with the rest of the spot fields (no extra step).
  detailsHeader = null,
  // Optional pre-validator for the Details tab. Lets the wrapper require
  // name/email before "Next" advances to Availability or "Add Spot" submits.
  validateDetailsExtra = null,
  // Optional interceptor for the API response. Receives (res, fd) and
  // returns true if it handled the response (skip default behavior), or
  // false/undefined to let AddSpotModal run its default success/error
  // path. Used by ClaimSpotModal to branch on email_exists.
  responseHandler = null,
  // Optional callback fired when the wizard advances a tab. Receives the
  // tab the user just left ("details" on Next, "availability" on Submit
  // after validation passes). Used by ClaimSpotModal for step analytics.
  onStepAdvance = null,
  // Landing wizard step-2 hook. When set, "Next" builds a details-only
  // FormData (no availability) and awaits this async before advancing.
  // Must resolve { ok: bool, spot?: { id, images: [url] } }; on ok the
  // returned spot's id + image URLs are merged into the local form so a
  // second save reuses the same row and skips already-uploaded images.
  onDetailsSave = null,
  // Landing wizard step-3 hook. When set, "Add Spot" bypasses the
  // multipart /spot/create + /spot/claim path and calls this with the
  // availability payload instead. Must resolve { ok: bool } — the wrapper
  // handles success toasts + navigation.
  onActivate = null,
  // Force the wizard button labels ("Next" / "Add Spot") even when spot
  // has an id (draft-resume case where the form is pre-filled).
  wizardMode = false,
}) => {
  const isEdit = Boolean(spot && spot.id);
  // Button labels: in wizardMode we always want "Next" / "Add Spot" even
  // when spot has an id (the draft-resume case). buildFormData still uses
  // isEdit to append id + imageOperations for the update semantic.
  const showWizardButtons = wizardMode || !isEdit;
  const [tab, setTab] = useState("details");
  const [form, setForm] = useState(() => buildInitial(spot));
  const [saving, setSaving] = useState(false);
  const [deletedImageUrls, setDeletedImageUrls] = useState([]);
  // Recurring editor: which day is selected, plus a draft for new time block.
  const [selectedDay, setSelectedDay] = useState("Mon");
  const [draftBlock, setDraftBlock] = useState({ startTime: "", endTime: "" });
  const [similarDays, setSimilarDays] = useState({
    show: false,
    startTime: "",
    endTime: "",
    pickedDays: [],
  });
  // Guards the two per-block async ops (applySimilarDays / addCustomDate)
  // so a rapid double-click can't submit the same block twice.
  const [applying, setApplying] = useState(false);
  // Per-block delete guard. Keys look like "recurring-Mon-0" / "custom-2".
  // A block whose key is in this set shows a spinner + is disabled — other
  // blocks stay clickable so users can queue multiple deletes.
  const [removingKeys, setRemovingKeys] = useState(() => new Set());
  const markRemoving = (key, on) =>
    setRemovingKeys((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  // Custom dates draft.
  const [draftDate, setDraftDate] = useState({
    date: "",
    startTime: "",
    endTime: "",
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    setForm(buildInitial(spot));
    setTab("details");
    setDeletedImageUrls([]);
  }, [spot]);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_API_KEY,
    libraries: ["places"],
  });

  const onField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleVehicleType = (k) =>
    setForm((f) => ({
      ...f,
      vehicleType: {
        ...f.vehicleType,
        [k]: !isVehicleTypeOn(f.vehicleType[k]),
      },
    }));

  const handleFiles = (files) => {
    if (!files || files.length === 0) return;
    const remainingSlots = 3 - form.images.length;
    if (remainingSlots <= 0) {
      showToast("error", "You can upload up to 3 images");
      return;
    }
    // Build the new image objects outside the state updater. React 18's
    // StrictMode runs setForm callbacks twice in dev, which would otherwise
    // create (and leak) two object URLs per file, and the caller's
    // input.value = "" clears the live FileList before the deferred
    // updater fires — so we snapshot synchronously up front too.
    const toAdd = Array.from(files)
      .slice(0, remainingSlots)
      .map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
        isExisting: false,
      }));
    setForm((f) => ({ ...f, images: [...f.images, ...toAdd] }));
  };

  const removeImage = (index) => {
    setForm((f) => {
      const img = f.images[index];
      if (img && img.isExisting && img.url) {
        setDeletedImageUrls((prev) => [...prev, img.url]);
      }
      if (img && img.previewUrl) URL.revokeObjectURL(img.previewUrl);
      return { ...f, images: f.images.filter((_, i) => i !== index) };
    });
  };

  const onPlacePicked = ({ address, lat, lng }) => {
    setForm((f) => ({
      ...f,
      address,
      location: { type: "Point", coordinates: [lng, lat] },
    }));
  };

  const dragMarker = (e) => {
    if (!e || !e.latLng) return;
    setForm((f) => ({
      ...f,
      location: {
        type: "Point",
        coordinates: [e.latLng.lng(), e.latLng.lat()],
      },
    }));
  };

  const refreshFromSpot = (spotData) => {
    if (!spotData) return;
    if (Array.isArray(spotData.availabilities)) {
      setForm((f) => ({
        ...f,
        availabilities: groupAvailabilityFromServer(spotData.availabilities),
      }));
    }
    if (Array.isArray(spotData.dateAvailabilities)) {
      setForm((f) => ({
        ...f,
        dateAvailabilities: spotData.dateAvailabilities.map((d) => ({
          id: d.id,
          date: d.date,
          startTime: d.startTime,
          endTime: d.endTime,
        })),
      }));
    }
  };

  // ----- recurring availability helpers -----
  const validateRecurringDraft = ({ startTime, endTime, day }) => {
    if (!startTime) {
      showToast("error", "Please select Open Time.");
      return false;
    }
    if (!endTime) {
      showToast("error", "Please select Close Time.");
      return false;
    }
    if (endTime <= startTime) {
      showToast(
        "error",
        "The close time should be greater than the open time.",
      );
      return false;
    }
    const dayEntry = form.availabilities.find((a) => a.day === day);
    if (!dayEntry) return false;
    if (overlaps(dayEntry.hours, startTime, endTime)) {
      showToast("error", "This time overlaps with an existing slot.");
      return false;
    }
    return true;
  };

  const addRecurringBlock = () => {
    const { startTime, endTime } = draftBlock;
    if (!validateRecurringDraft({ startTime, endTime, day: selectedDay })) {
      return;
    }

    // For both create AND edit mode, defer the actual save (or local insert)
    // until the user closes the Similar Days modal. That keeps the flow
    // identical to mobile: pick hours -> pick days to apply -> one save call.
    setDraftBlock({ startTime: "", endTime: "" });
    setSimilarDays({
      show: true,
      startTime,
      endTime,
      pickedDays: [],
    });
  };

  const toggleSimilarDay = (day) => {
    setSimilarDays((s) => {
      const has = s.pickedDays.includes(day);
      return {
        ...s,
        pickedDays: has
          ? s.pickedDays.filter((d) => d !== day)
          : [...s.pickedDays, day],
      };
    });
  };

  const applySimilarDays = async () => {
    if (applying) return;
    setApplying(true);
    try {
    const { startTime, endTime, pickedDays } = similarDays;

    if (isEdit) {
      const res = await API.createAvailability({
        spotId: form.id,
        day: selectedDay,
        startTime,
        endTime,
        similarDays: pickedDays,
        replaceOverlapping: pickedDays.length > 0 ? "ignore" : "false",
      });
      if (res && res.type === "success" && res.data) {
        refreshFromSpot(res.data);
        showToast("success", res.message || "Time block added");
      } else if (res && res.type === "conflict") {
        showToast(
          "error",
          res.message || "Time overlaps with an existing slot",
        );
      } else if (res && res.message) {
        showToast("error", res.message);
      }
    } else {
      // Create mode: insert the source day's slot, then attempt the picked
      // similar days with overlap-aware skipping.
      const skipped = [];
      setForm((f) => ({
        ...f,
        availabilities: f.availabilities.map((a) => {
          if (a.day === selectedDay) {
            return { ...a, hours: [...a.hours, { startTime, endTime }] };
          }
          if (pickedDays.includes(a.day)) {
            if (overlaps(a.hours, startTime, endTime)) {
              skipped.push(dayFullName(a.day));
              return a;
            }
            return { ...a, hours: [...a.hours, { startTime, endTime }] };
          }
          return a;
        }),
      }));
      if (skipped.length > 0) {
        showToast(
          "error",
          `Skipped ${skipped.join(", ")} (overlap with existing slot)`,
        );
      }
    }

    setSimilarDays({
      show: false,
      startTime: "",
      endTime: "",
      pickedDays: [],
    });
    } finally {
      setApplying(false);
    }
  };

  const removeRecurringBlock = async (day, idx) => {
    const key = `recurring-${day}-${idx}`;
    if (removingKeys.has(key)) return;
    const block = (
      form.availabilities.find((a) => a.day === day) || {
        hours: [],
      }
    ).hours[idx];

    markRemoving(key, true);
    try {
      if (isEdit && block && block.id) {
        const res = await API.deleteAvailability({
          spotId: form.id,
          id: block.id,
        });
        if (!res || res.type !== "success") {
          showToast("error", (res && res.message) || "Could not delete");
          return;
        }
        showToast("success", res.message || "Time block deleted");
      }

      setForm((f) => ({
        ...f,
        availabilities: f.availabilities.map((a) =>
          a.day === day
            ? { ...a, hours: a.hours.filter((_, i) => i !== idx) }
            : a,
        ),
      }));
    } finally {
      markRemoving(key, false);
    }
  };

  // ----- custom dates helpers -----
  const addCustomDate = async () => {
    if (applying) return;
    const { date, startTime, endTime } = draftDate;
    if (!date) {
      showToast("error", "Please choose a date.");
      return;
    }
    if (!startTime) {
      showToast("error", "Please select Open Time.");
      return;
    }
    if (!endTime) {
      showToast("error", "Please select Close Time.");
      return;
    }
    if (endTime <= startTime) {
      showToast(
        "error",
        "The close time should be greater than the open time.",
      );
      return;
    }
    const sameDate = form.dateAvailabilities.filter((d) => d.date === date);
    if (overlaps(sameDate, startTime, endTime)) {
      showToast(
        "error",
        "This time overlaps with an existing slot on this date.",
      );
      return;
    }

    setApplying(true);
    try {
      if (isEdit) {
        const res = await API.addDateAvailability({
          spotId: form.id,
          date,
          startTime,
          endTime,
        });
        if (!res || res.type !== "success") {
          showToast(
            "error",
            (res && res.message) || "Could not add date availability",
          );
          return;
        }
        if (res.data) refreshFromSpot(res.data);
        showToast("success", res.message || "Date availability added");
      } else {
        setForm((f) => ({
          ...f,
          dateAvailabilities: [
            ...f.dateAvailabilities,
            { date, startTime, endTime },
          ],
        }));
      }

      setDraftDate({ date: "", startTime: "", endTime: "" });
    } finally {
      setApplying(false);
    }
  };

  const removeCustomDate = async (idx) => {
    const key = `custom-${idx}`;
    if (removingKeys.has(key)) return;
    const slot = form.dateAvailabilities[idx];

    markRemoving(key, true);
    try {
      if (isEdit && slot && slot.id) {
        const res = await API.deleteDateAvailability({
          spotId: form.id,
          id: slot.id,
        });
        if (!res || res.type !== "success") {
          showToast("error", (res && res.message) || "Could not delete");
          return;
        }
        showToast("success", res.message || "Date availability deleted");
      }

      setForm((f) => ({
        ...f,
        dateAvailabilities: f.dateAvailabilities.filter((_, i) => i !== idx),
      }));
    } finally {
      markRemoving(key, false);
    }
  };

  // ----- validation + submit -----
  // Details-only checks. Used by the "Next" button on Add so the user can't
  // advance to the Availability tab with a half-filled details form, and
  // reused by validate() as the first half of the full pre-submit pass.
  const validateDetails = () => {
    // Wrapper-supplied checks run first (e.g. landing-page name/email).
    if (typeof validateDetailsExtra === "function") {
      if (!validateDetailsExtra()) return false;
    }
    if (form.images.length === 0) {
      showToast("error", "Please add at least one image of your spot");
      return false;
    }
    if (!form.name.trim()) {
      showToast("error", "Please enter spot name");
      return false;
    }
    if (!form.address.trim()) {
      showToast("error", "Please enter spot address");
      return false;
    }
    const priceField = form.isFlatRate ? form.flatRate : form.price;
    if (!priceField || isNaN(parseFloat(priceField))) {
      showToast(
        "error",
        form.isFlatRate ? "Please enter flat rate" : "Please enter spot rate",
      );
      return false;
    }
    const anyType =
      form.vehicleType.compact ||
      form.vehicleType.standard ||
      form.vehicleType.suv;
    if (!anyType) {
      showToast("error", "Please select vehicle type");
      return false;
    }
    return true;
  };

  const validate = () => {
    if (!validateDetails()) return false;

    if (form.availabilityMode === "custom_dates") {
      if (form.dateAvailabilities.length === 0) {
        setTab("availability");
        showToast("error", "Please add at least one date availability");
        return false;
      }
    } else {
      const anyHours = form.availabilities.some((a) => a.hours.length > 0);
      if (!anyHours) {
        setTab("availability");
        showToast("error", "Please enter spot availability");
        return false;
      }
    }
    return true;
  };

  const handleNext = async () => {
    if (!validateDetails()) return;

    // Landing wizard: persist a draft before advancing. onDetailsSave
    // owns the network call + toasts; we only advance on ok. On success
    // rehydrate id + image URLs so a back-then-Next reuses the same row
    // and skips already-uploaded images.
    if (typeof onDetailsSave === "function") {
      if (saving) return;
      setSaving(true);
      try {
        const fd = buildFormData({ omitAvailability: true });
        const result = await onDetailsSave(fd);
        if (!result || result.ok !== true) return;
        if (result.spot) {
          const savedImages = (result.spot.images || []).map((url) => ({
            url,
            isExisting: true,
          }));
          setForm((prev) => ({
            ...prev,
            id: result.spot.id || prev.id,
            images: savedImages.length > 0 ? savedImages : prev.images,
          }));
          setDeletedImageUrls([]);
        }
      } finally {
        setSaving(false);
      }
    }

    if (typeof onStepAdvance === "function") onStepAdvance("details");
    setTab("availability");
  };

  const buildFormData = ({ omitAvailability = false } = {}) => {
    const fd = new FormData();
    fd.append("name", form.name.trim());
    fd.append("address", form.address.trim());
    // Backend wants both fields; flat-rate just ignores the hourly value.
    fd.append("ratePerHour", form.price || "0");
    fd.append("isFlatRate", form.isFlatRate ? "true" : "false");
    fd.append("flatRate", form.flatRate || "0");
    fd.append("status", form.status);
    fd.append("currentDayOnly", String(form.currentDayOnly));
    fd.append("requiresApproval", form.requiresApproval ? "true" : "false");
    fd.append("hasEvCharger", form.hasEvCharger ? "true" : "false");

    fd.append(
      "allowedVehicleType[compact]",
      isVehicleTypeOn(form.vehicleType.compact) ? "true" : "false",
    );
    fd.append(
      "allowedVehicleType[standard]",
      isVehicleTypeOn(form.vehicleType.standard) ? "true" : "false",
    );
    fd.append(
      "allowedVehicleType[suv]",
      isVehicleTypeOn(form.vehicleType.suv) ? "true" : "false",
    );

    if (form.location.coordinates.length === 2) {
      fd.append("location[type]", "Point");
      fd.append(
        "location[coordinates][0]",
        String(form.location.coordinates[0]),
      );
      fd.append(
        "location[coordinates][1]",
        String(form.location.coordinates[1]),
      );
    }

    // Only NEW images go in the multipart body. Existing image URLs are
    // either kept implicitly or sent through imageOperations[delete].
    form.images.forEach((img) => {
      if (!img.isExisting && img.file) {
        fd.append("images", img.file);
      }
    });

    // Draft-resume + edit flows both include the spot id + delete ops so
    // the server updates the existing row instead of creating a new one.
    if (form.id) {
      fd.append("id", form.id);
      deletedImageUrls.forEach((url, i) => {
        fd.append(`imageOperations[delete][${i}]`, url);
      });
    }

    // /spot/update and /spot/save-draft both reject availability fields —
    // isEdit takes that path; omitAvailability is the wizard step-2 path.
    if (isEdit || omitAvailability) {
      return fd;
    }

    if (form.availabilityMode === "custom_dates") {
      fd.append("availabilityMode", "custom_dates");
      form.dateAvailabilities.forEach((slot, i) => {
        fd.append(`dateAvailabilities[${i}][date]`, slot.date);
        fd.append(`dateAvailabilities[${i}][startTime]`, slot.startTime);
        fd.append(`dateAvailabilities[${i}][endTime]`, slot.endTime);
      });
    } else {
      // Flatten {day, hours[]} -> {day, startTime, endTime} entries.
      let i = 0;
      form.availabilities.forEach((a) => {
        a.hours.forEach((h) => {
          fd.append(`availabilities[${i}][day]`, a.day);
          fd.append(`availabilities[${i}][startTime]`, h.startTime);
          fd.append(`availabilities[${i}][endTime]`, h.endTime);
          i += 1;
        });
      });
    }

    return fd;
  };

  const buildAvailabilityPayload = () => {
    if (form.availabilityMode === "custom_dates") {
      return {
        availabilityMode: "custom_dates",
        dateAvailabilities: form.dateAvailabilities.map((s) => ({
          date: s.date,
          startTime: s.startTime,
          endTime: s.endTime,
        })),
        availabilities: [],
      };
    }
    const flat = [];
    form.availabilities.forEach((a) => {
      a.hours.forEach((h) => {
        flat.push({ day: a.day, startTime: h.startTime, endTime: h.endTime });
      });
    });
    return {
      availabilityMode: "recurring",
      availabilities: flat,
      dateAvailabilities: [],
    };
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (saving) return;
    if (!validate()) return;

    if (typeof onStepAdvance === "function") onStepAdvance("availability");

    // Landing wizard: step 3 posts JSON to /spot/activate; images and
    // spot fields already landed in step 2's /spot/save-draft call.
    if (typeof onActivate === "function") {
      setSaving(true);
      try {
        const payload = {
          spotId: form.id,
          ...buildAvailabilityPayload(),
        };
        const result = await onActivate(payload);
        if (result && result.ok) {
          setForm(buildInitial(null));
          if (typeof onClose === "function") onClose();
        }
      } finally {
        setSaving(false);
      }
      return;
    }

    setSaving(true);
    const fd = buildFormData();

    // Optional extras (e.g. landing-page name/email lead capture).
    if (extraFields && typeof extraFields === "object") {
      Object.entries(extraFields).forEach(([k, v]) => {
        if (v !== undefined && v !== null) fd.append(k, String(v));
      });
    }

    // apiOverride lets the landing flow post to /spot/claim instead of
    // /spot/create without disturbing the authenticated host path.
    const defaultCall = isEdit ? API.updateSpot(fd) : API.createSpot(fd);
    const call = typeof apiOverride === "function" ? apiOverride(fd) : defaultCall;

    call
      .then((res) => {
        // Let the wrapper intercept first (used for email_exists branch).
        // If it returns true it owns the response; we bail out.
        if (typeof responseHandler === "function") {
          const handled = responseHandler(res, fd);
          if (handled) return;
        }

        if (res && res.type === "success") {
          trackEvent(isEdit ? "web_spot_updated" : "web_spot_created", {
            spot_id: (res.data && res.data.id) || form.id || undefined,
            is_flat_rate: !!form.isFlatRate,
          });
          showToast(
            "success",
            res.message || (isEdit ? "Spot updated" : "Spot created"),
          );
          // Pass the whole response on so the wrapper can read accessToken
          // and user from /spot/claim's success shape.
          if (typeof onSuccess === "function") onSuccess(res.data || {}, res);
          setForm(buildInitial(null));
          if (typeof onClose === "function") onClose();
        } else if (res && res.type === "conflict") {
          // Recurring availability collided with an existing block.
          // Mobile resets and closes; mirror that.
          showToast("error", res.message || "Time block conflict");
          setForm(buildInitial(null));
          if (typeof onClose === "function") onClose();
        } else {
          const details = res && res.details;
          const msg =
            (details && details[0] && details[0].message) ||
            (res && res.message) ||
            (isEdit ? "Failed to update spot" : "Failed to create spot");
          showToast("error", msg);
        }
      })
      .catch((err) =>
        showToast(
          "error",
          (err && err.message) ||
            (isEdit ? "Failed to update spot" : "Failed to create spot"),
        ),
      )
      .finally(() => setSaving(false));
  };

  const mapCenter = useMemo(() => {
    if (form.location.coordinates.length === 2) {
      return {
        lat: form.location.coordinates[1],
        lng: form.location.coordinates[0],
      };
    }
    return { lat: 38.905778, lng: -77.019382 };
  }, [form.location.coordinates]);

  const selectedDayHours =
    form.availabilities.find((a) => a.day === selectedDay)?.hours || [];

  const title = showWizardButtons ? "Add Spot" : "Update Spot";
  const submitLabel =
    successLabel || (showWizardButtons ? "Add Spot" : "Update Spot");

  return (
    <>
      <CustomModal
        show={show}
        title={title}
        onClose={saving ? null : onClose}
        className="add-spot-modal large"
      >
        <div className="text-center mb-3">
          <div className="pill-tabs">
            <button
              type="button"
              className={`pill-tab ${tab === "details" ? "active" : ""}`}
              onClick={() => setTab("details")}
            >
              Details
            </button>
            <button
              type="button"
              className={`pill-tab ${tab === "availability" ? "active" : ""}`}
              onClick={() => setTab("availability")}
            >
              Availability
            </button>
          </div>
        </div>

        {tab === "details" && (
          <Form>
            <div className="add-spot-images-wrapper text-center mb-3">
              <div
                className="add-spot-image-picker"
                onClick={() =>
                  form.images.length < 3 &&
                  fileInputRef.current &&
                  fileInputRef.current.click()
                }
              >
                {form.images.length === 0 ? (
                  <i className="fas fa-image" />
                ) : (
                  <img
                    src={
                      form.images[0].isExisting
                        ? form.images[0].url
                        : form.images[0].previewUrl
                    }
                    alt="Primary"
                    className="add-spot-image-primary"
                  />
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => {
                  handleFiles(e.target.files);
                  e.target.value = "";
                }}
              />
              <p className="add-spot-image-helper">
                Add one or more photos of your spot so parkers can find it easily.
              </p>

              {form.images.length > 0 && (
                <div className="add-spot-image-gallery">
                  {form.images.map((img, idx) => {
                    const src = img.isExisting ? img.url : img.previewUrl;
                    return (
                      <div
                        key={`spot-img-${idx}`}
                        className="add-spot-image-thumb"
                      >
                        <img src={src} alt="" />
                        <button
                          type="button"
                          className="add-spot-image-remove"
                          onClick={() => removeImage(idx)}
                        >
                          <i className="fas fa-times" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {detailsHeader}

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Spot Name</Form.Label>
              <Form.Control
                type="text"
                value={form.name}
                onChange={(e) => onField("name", e.target.value)}
                placeholder="Enter spot name"
                maxLength={80}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                Address{" "}
                <span className="text-muted">(Not shared until booked)</span>
              </Form.Label>
              <PlacesAutocomplete
                value={form.address}
                onChange={(v) => onField("address", v)}
                onSelect={onPlacePicked}
                placeholder="Search address"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <Form.Label className="fw-bold mb-0">
                  {form.isFlatRate ? "Flat Rate" : "Rate Per Hour"}{" "}
                  <span className="text-muted">(US Dollars)</span>
                </Form.Label>
                <Form.Check
                  type="switch"
                  id="add-spot-flat-rate-toggle"
                  label="Use Flat Rate"
                  checked={!!form.isFlatRate}
                  onChange={(e) => onField("isFlatRate", e.target.checked)}
                />
              </div>
              {form.isFlatRate ? (
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.flatRate}
                  onChange={(e) => onField("flatRate", e.target.value)}
                  placeholder="0.00"
                />
              ) : (
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => onField("price", e.target.value)}
                  placeholder="0.00"
                />
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                Allowed Vehicle Types (Select all that apply)
              </Form.Label>
              <div className="vehicle-type-row">
                {VEHICLE_TYPES.map((t) => {
                  const isActive = isVehicleTypeOn(form.vehicleType[t.value]);
                  return (
                    <div
                      key={`spot-vt-${t.value}`}
                      className="vehicle-type-item"
                      onClick={() => toggleVehicleType(t.value)}
                    >
                      <div
                        className={`vehicle-type-tile ${isActive ? "active" : ""}`}
                      >
                        <img src={t.img} alt={t.label} />
                      </div>
                      <p className="vehicle-type-label">{t.label}</p>
                    </div>
                  );
                })}
              </div>
            </Form.Group>

            {form.location.coordinates.length === 2 && isLoaded && (
              <div className="mb-3 add-spot-map">
                <GoogleMap
                  mapContainerStyle={{
                    width: "100%",
                    height: 200,
                    borderRadius: 10,
                  }}
                  center={mapCenter}
                  zoom={15}
                >
                  <Marker
                    position={mapCenter}
                    draggable
                    onDragEnd={dragMarker}
                  />
                </GoogleMap>
                <p className="text-muted small mb-0 mt-1">
                  Drag the marker to refine the exact location.
                </p>
              </div>
            )}

            <div className="add-spot-toggles">
              {/* Draft Mode toggle only makes sense when editing an
                  existing published spot to un-publish it. Hidden during
                  create (and wizard flow) — new spots go through the
                  save-draft → activate pipeline instead. */}
              {isEdit && !wizardMode && (
                <Form.Check
                  type="switch"
                  id="spot-draft"
                  label="Draft Mode"
                  checked={form.status === "draft"}
                  onChange={(e) =>
                    onField("status", e.target.checked ? "draft" : "published")
                  }
                />
              )}
              {/* <Form.Check
                type="switch"
                id="spot-day-only"
                label="Current Day Bookings Only"
                checked={form.currentDayOnly}
                onChange={(e) => onField("currentDayOnly", e.target.checked)}
              /> */}
              <Form.Check
                type="switch"
                id="spot-approval"
                label="Require approval before booking"
                checked={form.requiresApproval}
                onChange={(e) => onField("requiresApproval", e.target.checked)}
              />
              <Form.Check
                type="switch"
                id="spot-ev"
                label="EV charger available"
                checked={form.hasEvCharger}
                onChange={(e) => onField("hasEvCharger", e.target.checked)}
              />
            </div>
          </Form>
        )}

        {tab === "availability" && (
          <div className="add-spot-availability">
            {!isEdit && (
              <div className="availability-mode-tabs">
                <button
                  type="button"
                  className={`mode-tab ${form.availabilityMode === "recurring" ? "active" : ""}`}
                  onClick={() => onField("availabilityMode", "recurring")}
                >
                  Recurring Days
                </button>
                <button
                  type="button"
                  className={`mode-tab ${form.availabilityMode === "custom_dates" ? "active" : ""}`}
                  onClick={() => onField("availabilityMode", "custom_dates")}
                >
                  Individual Dates
                </button>
              </div>
            )}

            {form.availabilityMode === "recurring" && (
              <>
                <div className="day-selector mb-3">
                  {DAYS.map((d) => (
                    <button
                      type="button"
                      key={`day-${d.key}`}
                      className={`day-btn ${selectedDay === d.key ? "active" : ""}`}
                      onClick={() => setSelectedDay(d.key)}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>

                <Row className="g-2 mb-3">
                  <Col>
                    <Form.Label className="fw-bold mb-1">Open Time</Form.Label>
                    <Form.Control
                      type="time"
                      placeholder="Open Time"
                      value={draftBlock.startTime}
                      onChange={(e) =>
                        setDraftBlock((b) => ({
                          ...b,
                          startTime: e.target.value,
                        }))
                      }
                    />
                  </Col>
                  <Col>
                    <Form.Label className="fw-bold mb-1">Close Time</Form.Label>
                    <Form.Control
                      type="time"
                      placeholder="Close Time"
                      value={draftBlock.endTime}
                      onChange={(e) =>
                        setDraftBlock((b) => ({
                          ...b,
                          endTime: e.target.value,
                        }))
                      }
                    />
                  </Col>
                </Row>

                <button
                  type="button"
                  className="add-time-btn"
                  disabled={applying}
                  onClick={addRecurringBlock}
                >
                  {applying ? (
                    <>
                      <i className="fas fa-spin fa-spinner me-2" />
                      Adding…
                    </>
                  ) : (
                    "Add Time"
                  )}
                </button>

                <h6 className="day-hours-heading">
                  {dayFullName(selectedDay)}'s Hours
                </h6>

                <div className="time-block-list">
                  {selectedDayHours.length === 0 && (
                    <p className="day-hours-empty">
                      No hours added for {dayFullName(selectedDay)}
                    </p>
                  )}
                  {selectedDayHours.map((h, idx) => {
                    const rmKey = `recurring-${selectedDay}-${idx}`;
                    const isRemoving = removingKeys.has(rmKey);
                    return (
                      <div className="time-block-row" key={`block-${idx}`}>
                        <span>
                          {convertTimeTo12h(h.startTime)} –{" "}
                          {convertTimeTo12h(h.endTime)}
                        </span>
                        <Button
                          variant="link"
                          size="sm"
                          className="text-danger"
                          disabled={isRemoving}
                          onClick={() => removeRecurringBlock(selectedDay, idx)}
                        >
                          <span>
                            {isRemoving ? (
                              <i className="fas fa-spin fa-spinner" />
                            ) : (
                              "Remove"
                            )}
                          </span>
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {form.availabilityMode === "custom_dates" && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold mb-1">Date</Form.Label>
                  <Form.Control
                    type="date"
                    placeholder="Select Date"
                    value={draftDate.date}
                    onChange={(e) =>
                      setDraftDate((d) => ({ ...d, date: e.target.value }))
                    }
                  />
                </Form.Group>

                <Row className="g-2 mb-3">
                  <Col>
                    <Form.Label className="fw-bold mb-1">Open Time</Form.Label>
                    <Form.Control
                      type="time"
                      placeholder="Open Time"
                      value={draftDate.startTime}
                      onChange={(e) =>
                        setDraftDate((d) => ({
                          ...d,
                          startTime: e.target.value,
                        }))
                      }
                    />
                  </Col>
                  <Col>
                    <Form.Label className="fw-bold mb-1">Close Time</Form.Label>
                    <Form.Control
                      type="time"
                      placeholder="Close Time"
                      value={draftDate.endTime}
                      onChange={(e) =>
                        setDraftDate((d) => ({ ...d, endTime: e.target.value }))
                      }
                    />
                  </Col>
                </Row>

                <button
                  type="button"
                  className="add-time-btn"
                  disabled={applying}
                  onClick={addCustomDate}
                >
                  {applying ? (
                    <>
                      <i className="fas fa-spin fa-spinner me-2" />
                      Adding…
                    </>
                  ) : (
                    "Add Time"
                  )}
                </button>

                <div className="time-block-list mt-3">
                  {form.dateAvailabilities.length === 0 && (
                    <p className="day-hours-empty">
                      No custom date availability yet.
                    </p>
                  )}
                  {form.dateAvailabilities.map((d, idx) => {
                    const rmKey = `custom-${idx}`;
                    const isRemoving = removingKeys.has(rmKey);
                    return (
                      <div className="time-block-row" key={`cd-${idx}`}>
                        <span>
                          {d.date} · {convertTimeTo12h(d.startTime)} –{" "}
                          {convertTimeTo12h(d.endTime)}
                        </span>
                        <Button
                          variant="link"
                          size="sm"
                          className="text-danger"
                          disabled={isRemoving}
                          onClick={() => removeCustomDate(idx)}
                        >
                          <span>
                            {isRemoving ? (
                              <i className="fas fa-spin fa-spinner" />
                            ) : (
                              "Remove"
                            )}
                          </span>
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        <div className="d-grid mt-4">
          {tab === "details" && showWizardButtons ? (
            <Button
              type="button"
              variant=""
              disabled={saving}
              className="new-vehicle-submit"
              onClick={handleNext}
            >
              <span>
                {saving && <i className="fas fa-spin fa-spinner me-2" />}
                {saving ? "Saving…" : "Next"}
              </span>
            </Button>
          ) : (
            <Button
              type="button"
              variant=""
              disabled={saving}
              className="new-vehicle-submit"
              onClick={handleSubmit}
            >
              <span>
                {saving && <i className="fas fa-spin fa-spinner me-2" />}
                {saving ? "Saving..." : submitLabel}
              </span>
            </Button>
          )}
        </div>
      </CustomModal>

      {similarDays.show && (
        <CustomModal
          show={similarDays.show}
          title="Similar Days"
          onClose={() =>
            setSimilarDays({
              show: false,
              startTime: "",
              endTime: "",
              pickedDays: [],
            })
          }
          className="similar-days-modal"
        >
          <p className="text-muted small mb-2">
            Apply {convertTimeTo12h(similarDays.startTime)} –{" "}
            {convertTimeTo12h(similarDays.endTime)} to other days?
          </p>
          <div className="similar-days-bar">
            {DAYS.map((d) => {
              const isSelf = d.key === selectedDay;
              const isPicked = isSelf || similarDays.pickedDays.includes(d.key);
              return (
                <button
                  type="button"
                  key={`similar-${d.key}`}
                  className={`similar-day-btn ${isPicked ? "active" : ""}`}
                  disabled={isSelf}
                  onClick={() => !isSelf && toggleSimilarDay(d.key)}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="new-vehicle-submit w-100"
            disabled={applying}
            onClick={applySimilarDays}
          >
            <span>
              {applying && <i className="fas fa-spin fa-spinner me-2" />}
              {applying ? "Adding…" : "Add Time"}
            </span>
          </button>
        </CustomModal>
      )}
    </>
  );
};

export default AddSpotModal;
